import React from "react";
import { Button, Checkbox, InputNumber, Select, Tooltip } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FRACTIONS, describeTiers, tierErrors } from "./policyUtils";
import styles from "./BillingPolicies.module.css";

// Rows read "Up to [__] hours [including] → [charge]"; the last row is "Above that → [charge]".
const TierEditor = ({ tiers = [], onChange, readOnly }) => {
  const errors = tierErrors(tiers);

  const update = (index, patch) =>
    onChange(tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));

  const remove = (index) => onChange(tiers.filter((_, i) => i !== index));

  const addRule = () => {
    const bounded = tiers.slice(0, -1);
    const lastHours = bounded.length ? bounded[bounded.length - 1].up_to_hours || 0 : 0;
    const next = {
      up_to_hours: Math.min(lastHours + 2, 23),
      inclusive: false,
      fraction: bounded.length ? bounded[bounded.length - 1].fraction : 0,
    };
    onChange([...bounded, next, tiers[tiers.length - 1]]);
  };

  return (
    <div>
      {tiers.map((tier, index) => {
        const isLast = index === tiers.length - 1;
        return (
          <div key={index}>
            <div className={styles.tierRow}>
              {isLast ? (
                <span className={styles.tierLead}>
                  {tiers.length === 1 ? "Any leftover time" : "Above that"}
                </span>
              ) : (
                <>
                  <span className={styles.tierLead}>
                    {index === 0 ? "Up to" : "Then up to"}
                  </span>
                  <InputNumber
                    min={0.5}
                    max={23.5}
                    step={0.5}
                    value={tier.up_to_hours}
                    disabled={readOnly}
                    status={errors[index] ? "error" : undefined}
                    onChange={(value) => update(index, { up_to_hours: value })}
                    style={{ width: 80 }}
                  />
                  <span>hours</span>
                  <Tooltip title="Ticked: exactly this many hours falls in this rule. Unticked: it moves to the next rule.">
                    <Checkbox
                      checked={!!tier.inclusive}
                      disabled={readOnly}
                      onChange={(e) => update(index, { inclusive: e.target.checked })}
                    >
                      including
                    </Checkbox>
                  </Tooltip>
                </>
              )}
              <span className={styles.tierArrow}>→</span>
              <Select
                value={tier.fraction}
                options={FRACTIONS}
                disabled={readOnly}
                status={errors[index] ? "error" : undefined}
                onChange={(value) => update(index, { fraction: value })}
                style={{ width: 120 }}
              />
              {!isLast && !readOnly && (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  aria-label="Remove rule"
                  onClick={() => remove(index)}
                />
              )}
            </div>
            {errors[index] && <div className={styles.tierError}>{errors[index]}</div>}
          </div>
        );
      })}
      {!readOnly && tiers.length < 6 && (
        <Button type="link" icon={<PlusOutlined />} onClick={addRule} style={{ paddingLeft: 0 }}>
          Add rule
        </Button>
      )}
      <div className={styles.tierSummary}>{describeTiers(tiers)}</div>
    </div>
  );
};

export default TierEditor;
